import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Search, RefreshCw, Eye, MoreVertical, Play, Package, CheckCircle, Navigation, User, DollarSign, MapPin, FileText, Wallet, CreditCard, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { adminAPI, api } from '@/services/api';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useConstantsContext } from '@/contexts/ConstantsContext';
import { constantsUtils } from '@/hooks/useConstants';

const OperationsBookingsList = ({ onStatisticsUpdate, constants: constantsProp }) => {
  const navigate = useNavigate();
  // Utiliser les constantes passées en prop, sinon utiliser le contexte
  const constantsContext = useConstantsContext();
  const constants = constantsProp || constantsContext?.constants;
  const [searchTerm, setSearchTerm] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [transporterFilter, setTransporterFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState({ open: false, booking: null, action: null }); // 'start', 'deliver', 'complete'
  const [deleteDialog, setDeleteDialog] = useState({ open: false, booking: null });
  // État pour la gestion des paiements
  const [createPaymentDialogOpen, setCreatePaymentDialogOpen] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method_code: '',
    amount: '',
    phone_number: '',
    account_number: '',
    bank_name: '',
    reference: '',
    proof_file: null,
  });
  const [creatingPayment, setCreatingPayment] = useState(false);

  const isMountedRef = useRef(true);

  const loadBookings = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status_code = statusFilter;
      if (transporterFilter !== 'all') params.transporter = transporterFilter;

      const response = await adminAPI.getAllBookings(params);
      if (!isMountedRef.current) return;

      const bookingsData = response.data.results || response.data || [];
      setBookings(bookingsData);

      if (onStatisticsUpdate && isMountedRef.current) {
        const stats = {
          total: bookingsData.length,
          confirmed: bookingsData.filter(b => b.status_code === 'CONFIRMED').length,
          in_progress: bookingsData.filter(b => b.status_code === 'IN_PROGRESS').length,
          delivered: bookingsData.filter(b => b.status_code === 'DELIVERED').length,
          completed: bookingsData.filter(b => b.status_code === 'COMPLETED').length,
          cancelled: bookingsData.filter(b => b.status_code === 'CANCELLED').length,
        };
        onStatisticsUpdate(stats);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Erreur lors du chargement des bookings:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les missions',
        variant: 'destructive',
      });
      setBookings([]);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, transporterFilter]);

  useEffect(() => {
    isMountedRef.current = true;
    loadBookings();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, transporterFilter]);

  const handleStartMission = async () => {
    if (!actionDialog.booking) return;

    try {
      const response = await adminAPI.startMission(actionDialog.booking.id);
      toast({
        title: 'Succès',
        description: response.data?.detail || 'Mission démarrée avec succès',
      });
      setActionDialog({ open: false, booking: null, action: null });
      loadBookings();
    } catch (error) {
      console.error('Erreur lors du démarrage:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de démarrer la mission',
        variant: 'destructive',
      });
    }
  };

  const handleDeliverMission = async () => {
    if (!actionDialog.booking) return;

    try {
      const response = await adminAPI.deliverMission(actionDialog.booking.id);
      toast({
        title: 'Succès',
        description: response.data?.detail || 'Mission marquée comme livrée',
      });
      setActionDialog({ open: false, booking: null, action: null });
      loadBookings();
    } catch (error) {
      console.error('Erreur lors de la livraison:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de marquer la mission comme livrée',
        variant: 'destructive',
      });
    }
  };

  const handleCompleteMission = async () => {
    if (!actionDialog.booking) return;

    try {
      const response = await adminAPI.completeMission(actionDialog.booking.id);
      toast({
        title: 'Succès',
        description: response.data?.detail || 'Mission terminée avec succès',
      });
      if (response.data?.payment_warning) {
        toast({
          title: 'Avertissement',
          description: `Paiement incomplet (${response.data.payment_percentage}%). La mission a été terminée malgré tout.`,
          variant: 'default',
        });
      }
      setActionDialog({ open: false, booking: null, action: null });
      loadBookings();
    } catch (error) {
      console.error('Erreur lors de la complétion:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de terminer la mission',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status) => {
    if (!constants) return <Badge>{status}</Badge>;
    const label = constantsUtils.getLabel(constants.booking_status, status);
    const statusConfig = {
      'CONFIRMED': { color: 'bg-blue-500', icon: CheckCircle },
      'IN_PROGRESS': { color: 'bg-green-500', icon: Navigation },
      'DELIVERED': { color: 'bg-purple-500', icon: Package },
      'COMPLETED': { color: 'bg-gray-500', icon: CheckCircle },
      'CANCELLED': { color: 'bg-red-500', icon: CheckCircle },
      'DISPUTED': { color: 'bg-orange-500', icon: CheckCircle },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', icon: CheckCircle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M GNF`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K GNF`;
    }
    return `${amount?.toFixed(0) || 0} GNF`;
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const transporter = booking.transporter_detail || booking.transporter;
    const client = booking.client_detail || booking.client;
    return (
      booking.id?.toLowerCase().includes(searchLower) ||
      transporter?.organisation_name?.toLowerCase().includes(searchLower) ||
      transporter?.user?.email?.toLowerCase().includes(searchLower) ||
      client?.company_name?.toLowerCase().includes(searchLower) ||
      client?.user?.email?.toLowerCase().includes(searchLower)
    );
  });

  const getActionTitle = (action) => {
    switch (action) {
      case 'start': return 'Démarrer la mission';
      case 'deliver': return 'Marquer comme livrée';
      case 'complete': return 'Terminer la mission';
      default: return 'Action';
    }
  };

  const getActionDescription = (action) => {
    switch (action) {
      case 'start':
        return 'La mission passera au statut "En cours de livraison" et le véhicule sera marqué comme "En mission".';
      case 'deliver':
        return 'La mission passera au statut "Livrée" et la demande sera marquée comme "Terminée".';
      case 'complete':
        return 'La mission sera terminée et le véhicule sera remis disponible. Attention : le paiement n\'est peut-être pas complet.';
      default: return '';
    }
  };

  const handleAction = () => {
    switch (actionDialog.action) {
      case 'start':
        handleStartMission();
        break;
      case 'deliver':
        handleDeliverMission();
        break;
      case 'complete':
        handleCompleteMission();
        break;
    }
  };

  const handleDeleteBooking = async () => {
    if (!deleteDialog.booking) return;

    try {
      await adminAPI.deleteBooking(deleteDialog.booking.id);
      toast({
        title: 'Succès',
        description: 'Mission supprimée avec succès',
      });
      setDeleteDialog({ open: false, booking: null });
      loadBookings();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de supprimer la mission',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour ouvrir la modale de paiement du transporteur
  const handlePayTransporter = async (booking) => {
    try {
      const transporterPrice = booking.transporter_price || booking.agreed_price || 0;
      const transporter = booking.transporter_detail || booking.transporter;
      if (!transporter || !transporter.user) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer le transporteur.',
          variant: 'destructive',
        });
        return;
      }

      // Charger les détails complets du booking
      const response = await adminAPI.getBookingById(booking.id);
      const fullBooking = response.data;

      // Calculer le montant déjà payé au transporteur
      const transporterUserId = transporter.user;
      const transporterPayments = (fullBooking.payments || []).filter(
        payment => payment.payee_user === transporterUserId || payment.payee_user?.id === transporterUserId
      );
      const totalPaidToTransporter = transporterPayments
        .filter(p => p.status === 'SUCCESS')
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      const remainingAmount = Math.max(0, transporterPrice - totalPaidToTransporter);

      if (remainingAmount <= 0) {
        toast({
          title: 'Info',
          description: 'Le transporteur a déjà été entièrement payé.',
        });
        return;
      }

      setPaymentBooking(fullBooking);
      setPaymentData({
        method_code: '',
        amount: remainingAmount.toString(),
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });
      setCreatePaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'initialiser le paiement',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour valider les données de paiement
  const validatePaymentData = () => {
    if (!paymentData.method_code || !paymentData.amount) {
      return { valid: false, message: 'Veuillez sélectionner une méthode de paiement et entrer un montant.' };
    }

    switch (paymentData.method_code) {
      case 'ORANGE_MONEY':
      case 'MTN_MOMO':
        if (!paymentData.phone_number || paymentData.phone_number.length < 9) {
          return { valid: false, message: 'Veuillez entrer un numéro de téléphone valide (9 chiffres).' };
        }
        break;
      case 'BANK_TRANSFER':
        if (!paymentData.bank_name || !paymentData.account_number || !paymentData.reference) {
          return { valid: false, message: 'Veuillez remplir tous les champs du virement bancaire.' };
        }
        break;
      case 'CASH':
        if (!paymentData.proof_file) {
          return { valid: false, message: 'Veuillez télécharger une preuve de paiement pour le paiement en espèces.' };
        }
        break;
      default:
        return { valid: true };
    }

    return { valid: true };
  };

  // Fonction pour créer un paiement au transporteur
  const handleCreatePayment = async () => {
    if (!paymentBooking) {
      toast({
        title: 'Erreur',
        description: 'Aucune réservation sélectionnée.',
        variant: 'destructive',
      });
      return;
    }

    const validation = validatePaymentData();
    if (!validation.valid) {
      toast({
        title: 'Erreur',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreatingPayment(true);

      const transporter = paymentBooking.transporter_detail || paymentBooking.transporter;
      if (!transporter || !transporter.user) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer le transporteur.',
          variant: 'destructive',
        });
        return;
      }

      const payeeUserId = transporter.user;

      const paymentPayload = {
        booking: paymentBooking.id,
        payee_user: payeeUserId,
        amount: parseFloat(paymentData.amount),
        currency: 'GNF',
        method_code: paymentData.method_code,
        status: 'PENDING',
      };

      if (paymentData.method_code === 'CASH' && paymentData.proof_file) {
        const formData = new FormData();
        Object.keys(paymentPayload).forEach(key => {
          formData.append(key, paymentPayload[key]);
        });
        formData.append('proof', paymentData.proof_file);

        await api.post('/payments/payments/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        await api.post('/payments/payments/', paymentPayload);
      }

      toast({
        title: 'Paiement créé',
        description: 'Le paiement au transporteur a été créé avec succès.',
      });

      setCreatePaymentDialogOpen(false);
      setPaymentBooking(null);
      setPaymentData({
        method_code: '',
        amount: '',
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });

      loadBookings();
    } catch (err) {
      console.error('Erreur lors de la création du paiement:', err);
      toast({
        title: 'Erreur',
        description: err.response?.data?.detail || 'Impossible de créer le paiement.',
        variant: 'destructive',
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filtres et recherche */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par ID, client, transporteur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {constants?.booking_status?.map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadBookings} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Grille de cartes */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredBookings.length === 0 ? (
      <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aucune mission trouvée</h3>
            <p className="text-muted-foreground">Aucune mission ne correspond à vos critères de recherche.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredBookings.map((booking) => {
            const request = booking.request_detail || booking.request;
            const transporter = booking.transporter_detail || booking.transporter;
            const client = booking.client_detail || booking.client;
            const driver = booking.driver_detail || booking.driver;

            return (
              <Card key={booking.id} className="bg-card border border-border">
                <CardContent className="p-4 space-y-3">
                  {/* En-tête simple : RESERVATION et PAYMENT */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">RESERVATION</span>
                      <span className="text-sm font-bold">#{booking.id?.slice(-8) || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">PAYMENT</span>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-bold text-primary">{formatCurrency(booking.agreed_price || booking.price)}</span>
                        <span className="text-xs text-muted-foreground">Prix convenu</span>
                        {booking.transporter_price && booking.transporter_price !== booking.agreed_price && (
                          <>
                            <span className="text-sm font-bold text-secondary mt-1">{formatCurrency(booking.transporter_price)}</span>
                            <span className="text-xs text-muted-foreground">Prix transporteur</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Badges de statut */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(booking.status_code || booking.status)}
                    {booking.payment_percentage !== undefined && (
                      <Badge className={booking.payment_percentage >= 100 ? 'bg-green-500/20 text-green-400 border-green-500/30' : booking.payment_percentage > 0 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>
                        {booking.payment_percentage >= 100 ? 'Payé' : booking.payment_percentage > 0 ? 'Partiel' : 'Non payé'}
                      </Badge>
                    )}
                  </div>

                  {/* Barre de progression */}
                  {booking.payment_percentage !== undefined && (
                    <div>
                      <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            booking.payment_percentage >= 100 ? 'bg-green-500' :
                            booking.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(booking.payment_percentage, 100)}%` }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {booking.payment_percentage}% ({formatCurrency(booking.total_paid || 0)} / {formatCurrency(booking.agreed_price || booking.price)})
                      </div>
                    </div>
                  )}

                  {/* Visualisation de route simple */}
                  {request && (
                    <div className="flex items-center gap-2 py-2">
                      <div className="flex flex-col items-center flex-1">
                        <MapPin className="w-5 h-5 text-green-500" />
                        <span className="text-xs font-medium mt-1">
                          {request?.from_location_detail?.city || request?.from_location_detail?.label ||
                           request?.from_location?.city || request?.from_location?.label || 'N/A'}
                        </span>
                      </div>
                      <div className="flex-1 flex items-center justify-center">
                        <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                        <Truck className="w-5 h-5 text-green-500 mx-2" />
                        <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                      </div>
                      <div className="flex flex-col items-center flex-1">
                        <Navigation className="w-5 h-5 text-green-500" />
                        <span className="text-xs font-medium mt-1">
                          {request?.to_location_detail?.city || request?.to_location_detail?.label ||
                           request?.to_location?.city || request?.to_location?.label || 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Informations en deux colonnes */}
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                    {/* Colonne Transport */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <Truck className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-semibold">Transport</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        {request?.preferred_pickup_date && (
                          <div>
                            <span className="text-muted-foreground">Départ: </span>
                            <span className="font-medium">{formatDateOnly(request.preferred_pickup_date)}</span>
                          </div>
                        )}
                        {request?.preferred_delivery_date && (
                          <div>
                            <span className="text-muted-foreground">Arrivée: </span>
                            <span className="font-medium">{formatDateOnly(request.preferred_delivery_date)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">Véhicule: </span>
                          <span className="font-medium">{booking.vehicle_detail?.plate_number || booking.vehicle?.plate_number || '-'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Colonne Transporteur */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-semibold">Transporteur</span>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div>
                          <span className="text-muted-foreground">Chauffeur: </span>
                          <span className="font-medium">
                            {driver?.full_name ||
                             (driver?.first_name && driver?.last_name
                               ? `${driver.first_name} ${driver.last_name}`.trim()
                               : driver?.first_name || '-')}
                          </span>
                        </div>
                        {driver?.phone && (
                          <div>
                            <span className="text-muted-foreground">Tél: </span>
                            <span className="font-medium">{driver.phone}</span>
                          </div>
                        )}
                        {transporter?.organisation_name && (
                          <div>
                            <span className="text-muted-foreground">Entreprise: </span>
                            <span className="font-medium">{transporter.organisation_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Informations client (visible pour l'admin) */}
                  {client && (
                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center gap-1.5 mb-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-semibold">Client</span>
                      </div>
                      <div className="text-xs pl-5">
                        <span className="text-muted-foreground">
                          {client?.company_name ||
                           (client?.user?.first_name && client?.user?.last_name
                             ? `${client.user.first_name} ${client.user.last_name}`.trim()
                             : client?.user?.first_name || '-')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Termes */}
                  {request?.payment_terms && (
                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-semibold">Termes</span>
                      </div>
                      <div className="text-xs pl-5">
                        <span className="text-muted-foreground">Paiement: </span>
                        <span className="font-medium">
                          {constants?.payment_terms?.find(term => term[0] === request.payment_terms)?.[1] || request.payment_terms}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Section Paiements */}
                  <div className="pt-2 border-t border-border space-y-2">
                    {/* Paiements du client */}
                    {(() => {
                      const client = booking.client_detail || booking.client;
                      const clientUserId = client?.user || client?.user_detail?.id;
                      const clientPayments = (booking.payments || []).filter(
                        payment => payment.payer_user === clientUserId || payment.payer_user?.id === clientUserId
                      );
                      const clientPaymentsCount = clientPayments.length;
                      const clientTotalPaid = clientPayments
                        .filter(p => p.status === 'SUCCESS')
                        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

                      return (
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Paiements client:</span>
                            <span className="font-semibold">{formatCurrency(clientTotalPaid)}</span>
                            <span className="text-muted-foreground">({clientPaymentsCount})</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Paiement du transporteur */}
                    {(() => {
                      const transporter = booking.transporter_detail || booking.transporter;
                      const transporterUserId = transporter?.user;
                      const transporterPayments = (booking.payments || []).filter(
                        payment => payment.payee_user === transporterUserId || payment.payee_user?.id === transporterUserId
                      );
                      const transporterTotalPaid = transporterPayments
                        .filter(p => p.status === 'SUCCESS')
                        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                      const transporterPrice = booking.transporter_price || booking.agreed_price || 0;
                      const transporterRemaining = Math.max(0, transporterPrice - transporterTotalPaid);

                      return (
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground">Paiement transporteur:</span>
                            <span className="font-semibold">{formatCurrency(transporterTotalPaid)}</span>
                            {transporterRemaining > 0 && (
                              <>
                                <span className="text-muted-foreground">/</span>
                                <span className="font-semibold text-secondary">{formatCurrency(transporterPrice)}</span>
                                <span className="text-orange-500">(Restant: {formatCurrency(transporterRemaining)})</span>
                              </>
                            )}
                          </div>
                          {transporterRemaining > 0 && (
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handlePayTransporter(booking)}
                              className="h-6 text-xs bg-green-600 hover:bg-green-700"
                            >
                              <Wallet className="w-3 h-3 mr-1" />
                              Payer
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigate(`/admin/transporters`);
                      }}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Voir détails
                    </Button>
                    {(booking.status_code === 'CONFIRMED' || booking.status_code === 'IN_PROGRESS' || booking.status_code === 'DELIVERED') && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="default" size="sm" className="flex-1">
                            <MoreVertical className="w-4 h-4 mr-2" />
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {booking.status_code === 'CONFIRMED' && (
                            <DropdownMenuItem onClick={() => setActionDialog({ open: true, booking, action: 'start' })}>
                              <Play className="w-4 h-4 mr-2" />
                              Démarrer la mission
                            </DropdownMenuItem>
                          )}
                          {booking.status_code === 'IN_PROGRESS' && (
                            <DropdownMenuItem onClick={() => setActionDialog({ open: true, booking, action: 'deliver' })}>
                              <Package className="w-4 h-4 mr-2" />
                              Marquer comme livrée
                            </DropdownMenuItem>
                          )}
                          {booking.status_code === 'DELIVERED' && (
                            <DropdownMenuItem onClick={() => setActionDialog({ open: true, booking, action: 'complete' })}>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Terminer la mission
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog({ open: true, booking })}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {!(booking.status_code === 'CONFIRMED' || booking.status_code === 'IN_PROGRESS' || booking.status_code === 'DELIVERED') && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="default" size="sm" className="flex-1">
                            <MoreVertical className="w-4 h-4 mr-2" />
                            Actions
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setDeleteDialog({ open: true, booking })}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
        </CardContent>
      </Card>
            );
          })}
        </div>
      )}

      {/* Dialog pour les actions */}
      <AlertDialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getActionTitle(actionDialog.action)}</AlertDialogTitle>
            <AlertDialogDescription>
              {getActionDescription(actionDialog.action)}
              {actionDialog.action === 'complete' && actionDialog.booking && (
                <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm">
                    Paiement : {actionDialog.booking.payment_percentage || 0}%
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAction}
              className={
                actionDialog.action === 'start' ? 'bg-green-600 hover:bg-green-700' :
                actionDialog.action === 'deliver' ? 'bg-purple-600 hover:bg-purple-700' :
                'bg-gray-600 hover:bg-gray-700'
              }
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pour supprimer la mission */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-destructive/10 rounded-full">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <AlertDialogTitle className="text-xl font-bold text-destructive">
                ⚠️ ATTENTION - SUPPRESSION DÉFINITIVE
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3 pt-2">
              <p className="font-semibold text-foreground">
                Vous êtes sur le point de supprimer définitivement cette mission.
              </p>
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                <p className="font-medium text-destructive">
                  <strong>ID de la mission :</strong> {deleteDialog.booking?.id?.slice(-8) || 'N/A'}
                </p>
                {deleteDialog.booking?.agreed_price && (
                  <p className="text-sm">
                    <strong>Prix convenu :</strong> {formatCurrency(deleteDialog.booking.agreed_price)}
                  </p>
                )}
              </div>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                  ⚠️ Cette action est irréversible !
                </p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                  <li>La mission sera supprimée de manière permanente</li>
                  <li>Toutes les données associées seront perdues</li>
                  <li>Cette action ne peut pas être annulée</li>
                </ul>
              </div>
              <p className="text-sm font-medium text-foreground">
                Êtes-vous absolument certain de vouloir continuer ?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBooking}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Oui, supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog pour créer un paiement au transporteur */}
      <Dialog open={createPaymentDialogOpen} onOpenChange={setCreatePaymentDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-4">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Wallet className="w-6 h-6 text-primary" />
              </div>
              <span>Payer le transporteur</span>
            </DialogTitle>
            {paymentBooking && (
              <Card className="mt-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Mission</p>
                      <p className="font-semibold text-sm">#{paymentBooking.id?.slice(-8)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Prix transporteur</p>
                      <p className="font-semibold text-sm text-secondary">{formatCurrency(paymentBooking.transporter_price || paymentBooking.agreed_price || paymentBooking.price)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Déjà payé</p>
                      <p className="font-semibold text-sm text-secondary">
                        {formatCurrency(
                          (paymentBooking.payments || [])
                            .filter(p => {
                              const transporter = paymentBooking.transporter_detail || paymentBooking.transporter;
                              const transporterUserId = transporter?.user;
                              return (p.payee_user === transporterUserId || p.payee_user?.id === transporterUserId) && p.status === 'SUCCESS';
                            })
                            .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Montant restant</p>
                      <p className="font-semibold text-sm text-orange-500">{formatCurrency(paymentData.amount || 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Sélection de la méthode de paiement */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Méthode de paiement *</Label>
              <Select
                value={paymentData.method_code}
                onValueChange={(value) => setPaymentData({ ...paymentData, method_code: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une méthode" />
                </SelectTrigger>
                <SelectContent>
                  {constants?.payment_methods?.map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Montant */}
            <div>
              <Label className="text-sm font-semibold mb-2 block">Montant (GNF) *</Label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="Montant à payer"
              />
            </div>

            {/* Champs conditionnels selon la méthode */}
            {(paymentData.method_code === 'ORANGE_MONEY' || paymentData.method_code === 'MTN_MOMO') && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Numéro de téléphone *</Label>
                <Input
                  type="tel"
                  value={paymentData.phone_number}
                  onChange={(e) => setPaymentData({ ...paymentData, phone_number: e.target.value })}
                  placeholder="9 chiffres"
                />
              </div>
            )}

            {paymentData.method_code === 'BANK_TRANSFER' && (
              <>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Banque *</Label>
                  <Input
                    value={paymentData.bank_name}
                    onChange={(e) => setPaymentData({ ...paymentData, bank_name: e.target.value })}
                    placeholder="Nom de la banque"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Numéro de compte *</Label>
                  <Input
                    value={paymentData.account_number}
                    onChange={(e) => setPaymentData({ ...paymentData, account_number: e.target.value })}
                    placeholder="Numéro de compte"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold mb-2 block">Référence *</Label>
                  <Input
                    value={paymentData.reference}
                    onChange={(e) => setPaymentData({ ...paymentData, reference: e.target.value })}
                    placeholder="Référence du virement"
                  />
                </div>
              </>
            )}

            {paymentData.method_code === 'CASH' && (
              <div>
                <Label className="text-sm font-semibold mb-2 block">Preuve de paiement *</Label>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setPaymentData({ ...paymentData, proof_file: e.target.files?.[0] || null })}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePaymentDialogOpen(false)} disabled={creatingPayment}>
              Annuler
            </Button>
            <Button onClick={handleCreatePayment} disabled={creatingPayment}>
              {creatingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                'Créer le paiement'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsBookingsList;
